<template>
  <div class="page_title">
    <div class="align py-3 px-4">
      <h1 class="px-1">
        <i class="fa-regular fa-typewriter icon"></i>
        Redação
      </h1>
      <router-link :to="{ name: 'articleStepForm' }">
        <PrimaryButton class="px-5" icon="fa-solid fa-solid fa-plus">Nova notícia</PrimaryButton>
      </router-link>
    </div>
  </div>

  <div class="align p-4 space_title">

    <div v-if="!loading || articles.data.length" class="card">
      <div class="p-3" id="table_filter">
        <div class="row">
          <div class="col-sm-6 mb-2 mb-sm-0">
            <InputText placeholder="Buscar" v-model="filter.search" @keyup="changeFilter('search', filter.search)" />
          </div>

          <div class="col-sm-3 mb-2 mb-sm-0">
            <SelectBox
                id="status"
                placeholder="Todos os status"
                :items="statusOptions"
                v-model="filter.status"
                @change="changeFilter('status', filter.status)"
            />
          </div>

          <div class="col-sm-3">
            <SelectBox
                id="site"
                placeholder="Todos os sites"
                :items="sites"
                v-model="filter.site"
                @change="changeFilter('site', filter.site)"
            />
          </div>
        </div>
      </div>

      <div class="table_container">
      <table class="table_responsive">
        <thead>
          <tr>
            <th>ID</th>
            <th>Título</th>
            <th>Site</th>
            <th>Categoria</th>
            <th>Status</th>
            <th>Data</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(article, key) in articles.data" :key="key">
            <td>
              {{ article.id }}
            </td>
            <td>
              {{ article.title }}
            </td>
            <td class="text-nowrap">
              <i class="fa-solid fa-globe icon_label me-1"></i>
              {{ article.site_name }}
            </td>
            <td>
              <i class="fa-solid fa-tag icon_label me-1"></i>
              {{ article.category_name }}
            </td>
            <td>
              <span class="badge" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="true" data-bs-html="true" v-tooltip title="Agendada" :style="'background-color: ' + article.status_color"><i v-if="article.status == 'pending' && article.schedule_date" class="fa-solid fa-clock me-1"></i>{{ article.status_name }}</span>
            </td>
            <td class="text-nowrap">
              <i class="fa-solid fa-calendar-days icon_label me-1"></i>
              {{ $dateFormat(article.updated_at) }}
            </td>
            <td class="actions">
                <router-link v-if="article.status == 'draft' || article.status == 'refused' || (article.status == 'pending' && article.moderation == false)" :to="{ name: 'articleEdit', params: { id: article.id }}">
                  <button class="btn btn-secondary btn_action_responsive">
                    <span>Editar</span>
                    <i class="fa-regular fa-edit"></i>
                  </button>
                </router-link>
            </td>
          </tr>

          <tr class="no_result" v-if="!articles || (articles.data && !articles.data.length)">
            <td colspan="10">
              Nenhum resultado encontrado
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <ListPagination class="mt-4" :links="articles.meta.links" />

  </div>
</template>

<script setup>
import PrimaryButton from '@/components/PrimaryButton.vue'
import InputText from '@/components/InputText.vue'
import { onBeforeMount, onMounted, ref, watch } from 'vue';
import { useArticleStore } from '@/stores/ArticleStore'
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import ListPagination from '@/components/ListPagination.vue'
import { inject } from 'vue';
import SelectBox from "@/components/SelectBox.vue";
import { useSiteStore } from '@/stores/SiteStore'
import axios from 'axios';

const loader = inject('$loader');
const loading = ref(false);
let cancelTokenSource = null;
const statusOptions = ref([
  { id: null, 'name': 'Todas os status' },
  { id: 'pending', 'name': 'Pendente' },
  { id: 'publish', 'name': 'Publicada' },
  { id: 'refused', 'name': 'Recusada' },
  { id: 'draft', 'name': 'Rascunho' },
  { id: 'disabled', 'name': 'Desativada' },
  { id: 'reported', 'name': 'Reportada' },
  { id: 'blocked', 'name': 'Bloqueada' },
  { id: 'schedule', 'name': 'Agendada' },
]);

const articleStore = useArticleStore();
const { articles, filter } = storeToRefs(articleStore);

const siteStore = useSiteStore();
const sites = ref([]);

const router = useRouter();
const route = useRoute();

onBeforeMount(() => {
  filter.value = {
    search: null,
    status: null,
    site: null,
  };

  if (route.query.search) {
    filter.value.search = route.query.search;
  }

  if (route.query.status) {
    filter.value.status = route.query.status;
  }

  if (route.query.site) {
    filter.value.site = route.query.site;
  }

  loadArticles();
});

onMounted(async() => {
  await siteStore.getSitesList();
  sites.value = siteStore.sites;
  sites.value.unshift({id: null, name: 'Todos os sites'});
});

watch(() => route.query.page, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    window.scrollTo(0, 0);
    loadArticles();
  }
});

watch(() => [filter.value.search, filter.value.status, filter.value.site], debounce((newFilter, oldFilter) => {
      if (oldFilter.every((value) => value !== undefined) && newFilter.some((value, index) => value !== oldFilter[index])) {
        loadArticles();
      }
    }, 500)
);

function changeFilter(param, value){
  router.push({ query: { ...route.query, page: undefined, [param]: value } })
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

async function loadArticles() {
  loader.show();
  loading.value = true;
  filter.value.page = route.query.page ? parseInt(route.query.page) : null;

  if (cancelTokenSource) {
    cancelTokenSource.cancel('Cancelado devido a nova busca');
  }

  cancelTokenSource = axios.CancelToken.source();

  await articleStore.getArticles(cancelTokenSource);

  loader.hide();
  loading.value = false;
}
</script>